// src/editor/fields.tsx
// Small form controls for the visual editor. Each shows the spec's own
// value, or (greyed, as a placeholder) the value it inherits from a default
// or from style, and reports `undefined` when cleared, so the field is
// removed from the spec and the default applies again.
import { useEffect, useState, type ReactNode } from 'react';

interface NumberProps {
  label:     string;
  value:     unknown;
  /** What applies when the field is empty. */
  inherited?: number | string;
  min?:      number;
  max?:      number;
  step?:     number;
  suffix?:   string;
  onChange:  (v: number | undefined) => void;
  title?:    string;
}

/**
 * A number field that keeps what's typed while it's being typed ("1." or
 * "-"), and only reports a value once it's a number.
 */
export function NumberField({ label, value, inherited, min, max, step = 1, suffix, onChange, title }: NumberProps) {
  const external = typeof value === 'number' ? String(value) : '';
  const [text, setText] = useState(external);
  useEffect(() => { setText(t => (Number(t) === Number(external) && t !== '' && external !== '' ? t : external)); }, [external]);
  return (
    <label className="editor-field" title={title}>
      <span>{label}</span>
      <span className="editor-input-wrap">
        <input type="number" inputMode="decimal" value={text} min={min} max={max} step={step}
          placeholder={inherited === undefined ? '' : String(inherited)}
          onChange={e => {
            setText(e.target.value);
            if (e.target.value === '') onChange(undefined);
            else if (!Number.isNaN(e.target.valueAsNumber)) onChange(e.target.valueAsNumber);
          }} />
        {suffix && <span className="editor-suffix">{suffix}</span>}
      </span>
    </label>
  );
}

interface SelectProps<T extends string> {
  label:    string;
  value:    unknown;
  options:  readonly (T | [T, string])[];
  /** Shown as the first option, for "use the default / style's value". */
  inherit?: string;
  onChange: (v: T | undefined) => void;
}

export function SelectField<T extends string>({ label, value, options, inherit, onChange }: SelectProps<T>) {
  return (
    <label className="editor-field">
      <span>{label}</span>
      <select value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value === '' ? undefined : e.target.value as T)}>
        {inherit !== undefined && <option value="">{inherit}</option>}
        {options.map(o => {
          const [v, text] = Array.isArray(o) ? o : [o, o];
          return <option key={v} value={v}>{text}</option>;
        })}
      </select>
    </label>
  );
}

interface TextProps {
  label:     string;
  value:     unknown;
  inherited?: string;
  onChange:  (v: string | undefined) => void;
  /** Keep "" as a value instead of removing the field. */
  keepEmpty?: boolean;
  wide?:     boolean;
  title?:    string;
}

export function TextField({ label, value, inherited, onChange, keepEmpty, wide, title }: TextProps) {
  return (
    <label className={`editor-field${wide ? ' editor-field-wide' : ''}`} title={title}>
      <span>{label}</span>
      <input type="text" value={typeof value === 'string' ? value : ''} placeholder={inherited}
        onChange={e => onChange(e.target.value === '' && !keepEmpty ? undefined : e.target.value)} />
    </label>
  );
}

/** A colour: free text (make_video accepts #hex, names, rgb()/rgba()) plus a swatch picker for hex. */
export function ColourField({ label, value, inherited, onChange }: { label: string; value: unknown; inherited: string; onChange: (v: string | undefined) => void }) {
  const current = typeof value === 'string' ? value : inherited;
  const hex = /^#[0-9a-f]{6}$/i.test(current) ? current : /^#[0-9a-f]{3}$/i.test(current)
    ? '#' + current.slice(1).split('').map(c => c + c).join('') : '#000000';
  return (
    <label className="editor-field">
      <span>{label}</span>
      <span className="editor-colour">
        <input type="color" value={hex} onChange={e => onChange(e.target.value)} aria-label={`${label} swatch`} />
        <input type="text" value={typeof value === 'string' ? value : ''} placeholder={inherited}
          onChange={e => onChange(e.target.value === '' ? undefined : e.target.value)} />
      </span>
    </label>
  );
}

export function CheckField({ label, value, inherited = false, onChange }: { label: string; value: unknown; inherited?: boolean; onChange: (v: boolean | undefined) => void }) {
  const checked = typeof value === 'boolean' ? value : inherited;
  return (
    <label className="editor-check">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked === inherited ? undefined : e.target.checked)} />
      {label}
    </label>
  );
}

/** A collapsible editor section with optional actions in its header. */
export function Panel({ title, children, actions, open = true, active }: { title: string; children: ReactNode; actions?: ReactNode; open?: boolean; active?: boolean }) {
  return (
    <details className={`editor-panel${active ? ' editor-panel-active' : ''}`} open={open}>
      <summary>
        <span className="editor-panel-title">{title}</span>
        {actions && <span className="editor-panel-actions" onClick={e => e.preventDefault()}>{actions}</span>}
      </summary>
      <div className="editor-panel-body">{children}</div>
    </details>
  );
}
